import React, { useRef, useEffect } from 'react';
import { MUTATION, QUERY } from '../gql-operations';
import environment from '../env-config';
import { useMutation, useQuery } from '@apollo/client';
import { AuthContext } from '../util/context';
import useUpdateMessageMutation from './useUpdateMessageMutation';
import useUpdateCheckboxes from './useUpdateCheckboxes';
import useAddMetadata from '../hooks/useAddMetadata';
import customAnalytics from '../util/custom-analytics';
import useRedirectErrors from '../errors/useRedirectErrors';
import useS3Request from './useS3Request';
import { format } from 'date-fns';
import { isFileTypeImage, CLOUDINARY_SIZE_LIMIT } from '../constants/fileUtils';

import {
    getCloudinaryBackgroundsFolder,
    getCloudinaryProfilePictureFolder,
    getCloudinaryFileFolder
} from '../util/file-format-name';

function useCloudinaryRequest() {
    // Ref to track latest uploadProgress state (avoids stale closures)
    const uploadProgressRef = useRef({});
    const { updateCheckboxes } = useUpdateCheckboxes();
    const { passingErrors } = useRedirectErrors();
    const [createMessage] = useMutation(MUTATION.CREATE_MESSAGE, {
        onCompleted: res => {
            customAnalytics.message_sent(res.createMessage);
        },
        onError: error => {
            passingErrors(error);
            setClientError(error.graphQLErrors);
        }
    });
    const { data } = useQuery(QUERY.ME);
    const { updateFiles } = useUpdateMessageMutation();
    const { savedFromMeta } = useAddMetadata();
    const { sendToS3Files, sendImageToS3, updateMessageImage } = useS3Request();

    const {
        state: { uploadProgress },
        context: { setUploadProgress, setClientError }
    } = React.useContext(AuthContext);

    // Keep uploadProgress ref in sync with state
    useEffect(() => {
        uploadProgressRef.current = uploadProgress || {};
    }, [uploadProgress]);

    const makeCloudinaryReqest = (file, message) => {
        let cancelPromise;

        const uploadPromise = new Promise(async (resolve, reject) => {
            const xhr = new XMLHttpRequest();
            const progressKey = message ? `${message.id}_${isFileTypeImage(file.type) ? 'image' : 'file'}` : file.name;
            const fileType = isFileTypeImage(file.type) ? 'image' : 'file';

            // Helper to update progress using ref (avoids stale closure)
            const updateProgress = (state, percentage) => {
                const currentProgress = uploadProgressRef.current || {};
                const newProgress = {
                    ...currentProgress,
                    [progressKey]: { state, percentage, type: fileType }
                };
                uploadProgressRef.current = newProgress;
                setUploadProgress(newProgress);
            };

            // Set up cancellation
            cancelPromise = () => {
                xhr.abort();
                updateProgress('cancelled', 0);
                reject(new Error('Upload cancelled'));
            };

            xhr.upload.addEventListener('progress', event => {
                if (event.lengthComputable) {
                    const progressPercentage = (event.loaded / event.total) * 100;
                    updateProgress('pending', progressPercentage);
                }
            });

            xhr.addEventListener('load', () => {
                updateProgress('done', 100);
                // Clear progress after successful upload
                const { [progressKey]: removed, ...rest } = uploadProgressRef.current || {};
                uploadProgressRef.current = rest;
                setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                resolve(xhr.response);
            });

            xhr.addEventListener('error', () => {
                updateProgress('error', 0);
                reject(xhr.response);
            });

            xhr.addEventListener('abort', () => {
                updateProgress('cancelled', 0);
                reject(new Error('Upload aborted'));
            });

            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        try {
                            resolve(JSON.parse(xhr.response));
                        } catch (error) {
                            reject(error);
                        }
                    } else {
                        reject({
                            status: xhr.status,
                            statusText: xhr.statusText
                        });
                    }
                }
            };

            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('upload_preset', environment.REACT_APP_UPLOAD_PRESET);
                formData.append('folder', getCloudinaryFileFolder(data.me.id));

                xhr.open('POST', `https://api.cloudinary.com/v1_1/${environment.REACT_APP_CLOUD_NAME}/auto/upload`);
                xhr.send(formData);
            } catch (error) {
                reject(error);
            }
        });

        return {
            promise: uploadPromise,
            cancel: () => cancelPromise && cancelPromise()
        };
    };

    const prepareFiles = files => {
        return files.map(file => {
            if (file instanceof Blob || file instanceof File) {
                return file;
            }

            const objectToSend = {
                name: file.name || format(new Date(), 'yyyy-MM-dd_hh-mm-ss'),
                type: file.type,
                size: file.size,
                originalFile: file
            };

            if (file.height) objectToSend.height = file.height;
            if (file.width) objectToSend.width = file.width;

            return objectToSend;
        });
    };

    const sendImageMutation = async (file, values, folder, autoCheckbox, options = {}) => {
        try {
            // All images now go to S3 (no size limit) and stored as message.image
            // Server will generate Bunny URLs for optimization
            const createdMessage = await sendImageToS3(file, values, folder, autoCheckbox, options);

            // Clean up progress using ref
            const { [file.name]: removed, ...rest } = uploadProgressRef.current || {};
            uploadProgressRef.current = rest;
            setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            return createdMessage;
            
        } catch (error) {
            // Clean up progress using ref
            const { [file.name]: removed, ...rest } = uploadProgressRef.current || {};
            uploadProgressRef.current = rest;
            setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            throw error;
        }
    };

    const sendCloudinaryFiles = async (values, files, folder, autoCheckbox, options = {}) => {
        if (files) {
            const preparedFiles = prepareFiles(files);
            const createdMessages = [];
            const q = data?.me?.quota;
            const maxFileSize = q?.maxFileSize != null ? Number(q.maxFileSize) : null;
            const maxStorage = q?.maxStorage != null ? Number(q.maxStorage) : 0;
            const totalStorage = q?.totalStorage != null ? Number(q.totalStorage) : 0;
            const remainingStorage = maxStorage > 0 ? Math.max(0, maxStorage - totalStorage) : null;

            for (const file of preparedFiles) {
                if (!data) {
                    throw new Error('File size exceeds the maximum allowed limit');
                }
                if (maxFileSize != null && maxFileSize > 0 && file.size > maxFileSize) {
                    throw new Error('File size exceeds the maximum allowed limit');
                }
                if (remainingStorage != null && file.size > remainingStorage) {
                    throw new Error("You've reached the maximum storage limit. Delete some documents to free up space, or upgrade your account.");
                }
                const createdMessage = await sendImageMutation(file, values, folder, autoCheckbox, options);
                if (createdMessage?.id) {
                    createdMessages.push(createdMessage);
                }
            }
            return createdMessages;
        }
        return [];
    };

    const updateMessageImages = async (images, message) => {
        const files = Array.isArray(images) ? images : [images];
        await updateMessageImage(files, message);
    };

    return { prepareFiles, sendCloudinaryFiles, updateMessageImages };
}

export default useCloudinaryRequest;
